<script setup lang="ts">
import Badge from "../Badge.vue"
import { twMerge } from "tailwind-merge"

const props = withDefaults(
  defineProps<{
    start: Date | string
    end: Date | string
    badge?: boolean
    tooltip?: boolean
    symmetric?: boolean
    hideSeparator?: boolean
    ui?: { wrapper?: string }
  }>(),
  {
    badge: false,
    tooltip: false,
    symmetric: false,
    hideSeparator: false,
    ui: () => ({}),
  },
)

const start = computed(() => toDateTZ(props.start))
const end = computed(() => toDateTZ(props.end))

const isSingleExactMonth = computed<boolean>(
  () =>
    dt.isEqual(getDateAsFromTz(dt.addMonths(start.value, 1)), end.value) &&
    dt.isEqual(getDateAsFromTz(dt.startOfMonth(start.value)), start.value),
)

const flexContainer = ref<HTMLElement | null>(null)

const { locale } = useI18n()

const binds = computed(() => {
  const baseBinds = {
    class: twMerge(
      "inline-flex items-center justify-center gap-x-1 flex-wrap",
      props.ui?.wrapper || "",
    ),
    ref: flexContainer,
  }
  const extraBinds = props.badge ? { color: "rnxblue", button: false } : {}
  return {
    ...baseBinds,
    ...extraBinds,
  }
})
</script>

<template>
  <component :is="tooltip ? 'tippy' : 'div'" interactive>
    <component :is="props.badge ? Badge : 'div'" v-if="isSingleExactMonth" v-bind="binds">
      {{ $d(start, "monthYear", locale) }}
    </component>

    <component
      :is="props.badge ? Badge : 'div'"
      v-else-if="start.getFullYear() === end.getFullYear() && !symmetric"
      v-bind="binds"
    >
      <!-- Badge renders its slot inside a non-flex span, where `gap-x-1` has no effect: explicit
           spaces separate the parts there, while a flex container drops them. -->
      <span>{{ $d(start, "dayMonth", locale) }}</span>
      {{ " " }}
      <template v-if="!hideSeparator">
        <span>-</span>
        {{ " " }}
      </template>
      <span>{{ $d(end, "dayMonth", locale) }}</span>
      {{ " " }}
      <span>{{ end.getFullYear() }}</span>
    </component>

    <component :is="props.badge ? Badge : 'div'" v-else v-bind="binds">
      <span>{{ $d(start, "dayMonthYear", locale) }}</span>
      {{ " " }}
      <template v-if="!hideSeparator">
        <span>-</span>
        {{ " " }}
      </template>
      <span>{{ $d(end, "dayMonthYear", locale) }}</span>
    </component>

    <template v-if="tooltip" #content>
      <div class="inline-flex items-center gap-x-1">
        <IsoDate mode="short" :value="start" />
        <UIcon name="i-heroicons-arrow-right-20-solid" />
        <IsoDate mode="short" :value="end" />
      </div>
    </template>
  </component>
</template>
